import { CircularProgress } from '@mui/material'
import { useContext } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import ConfirmationModal from 'src/consumer/components/helperComponents'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import useClinicLocationsByBrand from 'src/mentor/components/MentorPrescriptionTemplates/components/hooks/useClinicLocationsByBrand'
import usePrescriptionTemplateFormStates from 'src/mentor/components/MentorPrescriptionTemplates/components/hooks/usePrescriptionTemplateFormStates'
import PrescriptionTemplateForm from 'src/mentor/components/MentorPrescriptionTemplates/components/PrescriptionTemplateForm'
import PrescriptionViewPageHeading from 'src/mentor/components/MentorPrescriptionTemplates/components/PrescriptionViewPageHeading'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  AvailabilityPageContainer,
  PageHeadlineComponent,
} from '../../MentorAvailabilityPage/MentorAvailabilityPageStyles'

// On desktop, pin the whole editor to the viewport height so the page itself does NOT
// scroll: the common header (clinic selector / tabs) stays fixed and the two panes below
// (left controls + right preview) each own an independent scrollbar. This is pure CSS — no
// JS measurement — so it can't drift or collapse. Below 1100px we release the fixed height
// and let the page scroll normally (the panes stack vertically).
const EditorPageContainer = styled(AvailabilityPageContainer)`
  @media (min-width: 1101px) {
    /* flex:none is REQUIRED: AvailabilityPageContainer sets flex:1 (flex-basis:0%), which
       in the parent ContentContainer's column layout would override our explicit height,
       leaving the box content-sized and collapsing both panes into one page scroll.
       flex:none restores flex-basis:auto so height:100dvh is the definite, bounded size. */
    flex: none;
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }
  @media print {
    flex: none;
    height: auto;
    min-height: 0;
    overflow: visible;
  }
`

const EditorFormArea = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  @media (min-width: 1101px) {
    flex: 1 1 auto;
    min-height: 0;
  }
  @media print {
    display: block;
  }
`

export default function PrescriptionViewPage() {
  const { clinicId } = useParams()
  const [searchParams] = useSearchParams()
  const eventId = searchParams.get('eventId')
  const { mentorClinicAssociationData, mentorProfileData } = useContext(MentorProfileContext)
  const { unSecureSetupData } = useContext(UnsecureSetupContext)

  const { clinicLocationOptions, clinicBrands } = useClinicLocationsByBrand({
    mentorClinicAssociationData,
  })
  const {
    handlers,
    loading,
    printPreviewType,
    setPrintPreviewType,
    paperPrescriptionTemplate,
    setPaperPrescriptionTemplate,
    pdfPrescriptionTemplate,
    setPdfPrescriptionTemplate,
    addTemplate,
    deleteTemplate,
    activateTemplate,
    pdfOptions,
    paperOptions,
    paperPrintSettings,
    handleSubmit,
    savePrescriptionTemplate,
    confirmationModalProps,
  } = usePrescriptionTemplateFormStates({
    clinicId,
    clinicBrands,
    mentorClinicAssociationData,
    mentorProfileData,
  })

  // Clone the Paper template's design into the Digital (pdf) template — keeps the digital
  // template's own identity (id / active / type) and replaces everything else with the
  // paper config. Used by the "Copy from Paper" button on the Digital Rx tab.
  const copyFromPaperTemplate = () => {
    if (!paperPrescriptionTemplate) return
    setPdfPrescriptionTemplate((prev) => ({
      ...paperPrescriptionTemplate,
      id: prev?.id ?? undefined,
      prescriptionTemplateType: 'pdf',
      active: prev?.active,
      title: prev?.title ?? paperPrescriptionTemplate?.title,
    }))
  }

  // const {
  //   confirmationModalProps,
  //   handleSubmit,
  //   savePrescriptionTemplate,
  //   activatePrescriptionTemplate,
  //   createPrescriptionTemplate,
  //   handleDelete,
  // } = usePrescriptionTemplateActions({
  //   clinicId,
  //   mentorClinicAssociationData,
  //   mentorProfileData,
  // })
  // Custom hooks for handlers and save logic

  return (
    <>
      <style>
        {`
          @media print {
            /* Hide all scrollbars */
            * {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            *::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
            /* Remove all shadows */
            * {
              box-shadow: none !important;
            }
            /* Ensure no overflow issues */
            body {
              overflow: visible !important;
            }
            html {
              overflow: visible !important;
            }
            /* Remove any right-side spacing that might be caused by scrollbars */
            body, html {
              margin-right: 0 !important;
              padding-right: 0 !important;
            }
          }
        `}
      </style>
      <EditorPageContainer>
        <PageHeadlineComponent
          pageContainerCustomStyles={{ marginBottom: '0px' }}
          fullWidthHeader
          heading={
            <PrescriptionViewPageHeading
              clinicId={clinicId}
              clinicLocationOptions={clinicLocationOptions}
              mentorClinicAssociationData={mentorClinicAssociationData}
            />
          }
        />
        {loading && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              maxWidth: '768px',
              height: '100%',
              margin: '30px auto',
            }}
          >
            <CircularProgress />
          </div>
        )}
        {!loading && (
          // Pass down the clinic location here. EditorFormArea fills the space below the
          // fixed header (flex:1, min-height:0 on desktop) so the form's split panes can
          // each scroll independently without coupling to the page.
          <EditorFormArea>
            <PrescriptionTemplateForm
              printPreviewType={printPreviewType}
              copyFromPaperTemplate={copyFromPaperTemplate}
              canCopyFromPaper={Boolean(paperPrescriptionTemplate)}
              pdfOptions={pdfOptions}
              printOptions={paperOptions}
              setPrintPreviewType={setPrintPreviewType}
              prescriptionTemplate={
                printPreviewType === 'print' ? paperPrescriptionTemplate : pdfPrescriptionTemplate
              }
              setPrescriptionTemplate={
                printPreviewType === 'print'
                  ? setPaperPrescriptionTemplate
                  : setPdfPrescriptionTemplate
              }
              templateActions={{
                addTemplate,
                deleteTemplate,
                activateTemplate,
              }}
              handlers={handlers}
              customOptions={unSecureSetupData}
              disabled={false}
              isUsingNewPrintMethod={paperPrintSettings?.useNewPrintMethod}
              handleSubmit={handleSubmit}
              eventId={eventId}
              mentorProfileData={mentorProfileData}
              savePrescriptionTemplate={savePrescriptionTemplate}
              clinicId={clinicId}
            />
            <ConfirmationModal {...confirmationModalProps} newImplementation />
          </EditorFormArea>
        )}
      </EditorPageContainer>
    </>
  )
}
